import React, { useEffect, useState, useCallback, useRef } from "react";
import { WeeklySummaryCard } from "../components/WeeklySummaryCard";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { DailySummaryCard } from "../components/DailySummaryCard";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Sparkles, RefreshCw, Loader2, CalendarDays } from "lucide-react";

const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function DailySummaryPage() {
  const { user, t } = useApp();
  const [date, setDate] = useState(() => fmtD(new Date()));
  const [items, setItems] = useState(null);
  const [checked, setChecked] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const autoTried = useRef({});
  const today = fmtD(new Date());

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [listRes, stRes] = await Promise.all([
        api.get(`/daily-summaries?date=${date}&user_id=${user.id}`),
        api.get(`/daily-summaries/status?date=${date}`).catch(() => ({ data: { checked: false } })),
      ]);
      setChecked(!!stRes.data?.checked);
      setItems(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (e) {
      setChecked(false);
      setItems([]);
    }
  }, [date, user.id]);

  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async (force = false) => {
    if (genBusy) return;
    setGenBusy(true);
    try {
      const r = await api.post("/daily-summaries/generate", { date, force }, { timeout: 180000 });
      if ((r.data?.generated || 0) === 0) toast.info(t("summary_none_activity"));
      await load();
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setGenBusy(false);
    }
  }, [genBusy, date, load, t]);

  // Once saved, always saved: if this day was never checked, generate it
  // AUTOMATICALLY on open (a single time — the run marker persists even for
  // days without activity, so the app never asks again).
  useEffect(() => {
    if (items === null || checked || genBusy) return;
    if (items.length > 0 || date > today) return;
    if (autoTried.current[date]) return;
    autoTried.current[date] = true;
    generate(false);
  }, [items, checked, date, today, genBusy, generate]);

  const shiftDay = (delta) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    const next = fmtD(d);
    if (next > today) return;
    setDate(next);
  };

  const mine = (items || []).filter((s) => s.supervisor_id === user.id);
  const others = (items || []).filter((s) => s.supervisor_id !== user.id);

  return (
    <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-accent-foreground" />
        <h1 className="font-display font-bold text-2xl tracking-tight">{t("daily_summary")}</h1>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t("summary_hint")}</p>

      <WeeklySummaryCard />

      {/* Date navigation */}
      <div className="flex items-center gap-2 mb-5">
        <Button variant="outline" size="icon" className="h-9 w-9" data-testid="summary-prev-day" onClick={() => shiftDay(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="relative">
          <CalendarDays className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            data-testid="summary-date-input"
            type="date"
            value={date}
            max={today}
            onChange={(e) => e.target.value && e.target.value <= today && setDate(e.target.value)}
            className="h-9 w-[170px] pl-8 text-sm"
          />
        </div>
        <Button variant="outline" size="icon" className="h-9 w-9" data-testid="summary-next-day" onClick={() => shiftDay(1)} disabled={date >= today}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        {date === today && <Badge variant="secondary" className="text-[10px]">{t("today")}</Badge>}
        <div className="flex-1" />
        {items !== null && items.length > 0 && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" data-testid="summary-refresh" onClick={() => generate(true)} disabled={genBusy}>
            {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            {t("summary_refresh")}
          </Button>
        )}
      </div>

      {/* Loading / auto-generating */}
      {(items === null || (genBusy && (items || []).length === 0)) && (
        <div className="space-y-3">
          {genBusy && (
            <Card className="p-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="summary-autogen">
              <Loader2 className="h-4 w-4 animate-spin text-primary" /> {t("summary_generating")}
            </Card>
          )}
          {[1, 2, 3].map((i) => <Card key={i} className="h-28 animate-pulse bg-muted" />)}
        </div>
      )}

      {/* Day already checked and truly empty — saved marker, never asks again */}
      {items !== null && items.length === 0 && checked && !genBusy && (
        <Card className="p-8 text-center" data-testid="summary-empty">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{t("summary_none_activity")}</p>
          <Button variant="ghost" size="sm" data-testid="summary-recheck-btn" onClick={() => generate(true)} disabled={genBusy}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> {t("summary_recheck")}
          </Button>
        </Card>
      )}

      {/* Never checked (e.g. future-proof fallback) — manual generate */}
      {items !== null && items.length === 0 && !checked && !genBusy && (
        <Card className="p-8 text-center" data-testid="summary-empty-unchecked">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{t("summary_empty")}</p>
          <Button data-testid="summary-generate-btn" onClick={() => generate(false)} disabled={genBusy}>
            <Sparkles className="h-4 w-4 mr-2" /> {t("summary_generate")}
          </Button>
        </Card>
      )}

      {/* My jobs first */}
      {mine.length > 0 && (
        <>
          <h2 className="font-display font-semibold text-base mb-2" data-testid="summary-section-mine">{t("my_jobs")}</h2>
          <div className="space-y-3 mb-6">
            {mine.map((s) => <DailySummaryCard key={s.id} summary={s} />)}
          </div>
        </>
      )}
      {others.length > 0 && (
        <>
          <h2 className="font-display font-semibold text-base mb-2 text-muted-foreground" data-testid="summary-section-others">{t("other_supervisors_jobs")}</h2>
          <div className="space-y-3">
            {others.map((s) => <DailySummaryCard key={s.id} summary={s} />)}
          </div>
        </>
      )}
    </div>
  );
}
